import React from "react";
import { motion } from "framer-motion";
import { Heart, Clock, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import StatusChip from "@/components/shared/StatusChip";
import { cn } from "@/lib/utils";
import type { Tip } from "@/hooks/useTips";

interface TipCardProps {
  tip: Tip;
  variant?: "compact" | "full";
  showStudent?: boolean;
  showPartner?: boolean;
  onClick?: () => void;
  className?: string;
}

const TYPE_LABELS: Record<string, string> = {
  appreciation: "Appreciation",
  milestone: "Milestone",
};

type LucideIcon = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

const TYPE_CONFIG: Record<string, { variant: "warning" | "info"; icon: LucideIcon }> = {
  appreciation: {
    variant: "warning",
    icon: Heart,
  },
  milestone: {
    variant: "info",
    icon: Star,
  },
};

const formatTZS = (amount: number) =>
  new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
  }).format(amount);

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export default function TipCard({
  tip: tipProp,
  variant = "compact",
  showStudent = false,
  showPartner = false,
  onClick,
  className,
}: TipCardProps) {
  const tip = tipProp as Tip & { student_email?: string };
  const typeConfig = TYPE_CONFIG[tip.type || "appreciation"] || TYPE_CONFIG.appreciation;
  const TypeIcon = typeConfig.icon;

  return (
    <motion.div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        "flex flex-col gap-3 rounded-md border border-border bg-card transition-colors",
        variant === "full" ? "p-4 md:p-5" : "p-3.5",
        onClick &&
          "cursor-pointer hover:border-hairline-strong hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      {/* Header: icon + who + type badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground"
            aria-hidden
          >
            <TypeIcon size={15} />
          </span>
          {showStudent && (
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-foreground">
                {tip.student_name || "Student"}
              </p>
              {tip.student_email && (
                <p className="truncate text-[11px] text-muted-foreground">{tip.student_email}</p>
              )}
            </div>
          )}
          {showPartner && (
            <p className="truncate text-[13px] font-medium text-foreground">
              {tip.partner_name || "Partner"}
            </p>
          )}
          {!showStudent && !showPartner && (
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-foreground">
                {tip.partner_name || tip.student_name || "—"}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {tip.partner_name ? "From partner" : "Received"}
              </p>
            </div>
          )}
        </div>
        <Badge variant={typeConfig.variant} className="shrink-0">
          <TypeIcon />
          {TYPE_LABELS[tip.type || "appreciation"] || "Appreciation"}
        </Badge>
      </div>

      {/* Message */}
      {tip.message && (
        <p
          className={cn(
            "leading-relaxed text-body",
            variant === "full" ? "text-sm" : "text-[13px]",
          )}
        >
          "{tip.message}"
        </p>
      )}

      {/* Footer: amount + date */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={cn(
            "font-mono font-medium tabular-nums text-foreground",
            variant === "full" ? "text-base" : "text-[15px]",
          )}
        >
          {formatTZS(tip.amount)}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums">
          <Clock className="size-3" aria-hidden />
          {formatDate(tip.created_at)}
        </span>
      </div>

      {/* Status badge for full variant */}
      {variant === "full" && (
        <div className="flex items-center gap-2">
          <StatusChip status={tip.status} />
        </div>
      )}
    </motion.div>
  );
}
